// SessionsDevMode.tsx
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useCommonComponents } from "@me8eon/components";
import { useAttributeValueComponents, useRenderers } from "@me8eon/renderers";
import { SessionAndMessagesContext, useAsyncSessionAndMessages, useSessionAndMessages } from "./session.messages.store";
import { useUserDataEmail } from "@me8eon/react_authentication";
import { EonUiButton } from "@eon-ui/eon-ui-components-react";
import { SimpleSelectableButtonBar } from "@me8eon/selection_button";
import { useCrytoStore } from "@me8eon/react_storage";
import { isValue } from "@me8eon/errors";
import { aesDecodeFn, aesEncodeFn } from "@enterprise_search/storage_crypto";

// Optional: keep this if referenced elsewhere
const SessionsMode = ["SessionsAndMessage", "SessionCrypto", "MessagesCrypto", "SessionEncryptDecrypt", "MessagesEncryptDecrypt"];

/* ---------------------------- Local data hook ---------------------------- */

type AnyObj = Record<string, unknown>;

export type SessionsAndMessagesOps = {
    deleteAllSessions: () => Promise<void>;
    breakIt: () => Promise<void>;
    nukeAllSessions: () => Promise<void>;
};

export type SessionsAndMessagesData = {
    state: AnyObj | undefined;
    messages: AnyObj | undefined;
    sessionsAndMessages: AnyObj | undefined;
    parentSessions: Array<{ id: string }>;
    errors: any;
    setErrors: React.Dispatch<React.SetStateAction<any>>;
    ops: SessionsAndMessagesOps;
};

/** Centralised place to get/shape "sessions & messages" data + ops */
function useSessionsAndMessagesData(isRecentChatIssue?: boolean): SessionsAndMessagesData {
    const [state] = useAsyncSessionAndMessages();
    const [sessionsAndMessages] = useSessionAndMessages();
    const [errors, setErrors] = useState<any>("");

    const sessionAndMessagesContext = useContext(SessionAndMessagesContext);
    const email = useUserDataEmail();

    const messages = (sessionsAndMessages as AnyObj | undefined)?.child as AnyObj | undefined;
    const parentSessions =
        ((sessionsAndMessages as AnyObj | undefined)?.parent as AnyObj | undefined)?.sessions as
            | Array<{ id: string }>
            | undefined;

    const deleteAllSessions = useCallback(async () => {
        if (confirm(`Are you sure you want to delete all sessions for ${email}?`)) {
            console.log("Deleting all sessions for", email);
            Object.keys(localStorage).forEach((key) => {
                if (key.startsWith("session-")) {
                    console.log(`Deleting session key: ${key}`);
                    localStorage.removeItem(key);
                }
            });

            const ids = (parentSessions ?? []).map((s: any) => s.id);
            const deleteChild = await sessionAndMessagesContext?.ops?.deleteChild?.(...ids);
            const deleteParentAndChildren =
                await sessionAndMessagesContext?.store?.deleteParentAndChildren?.(
                    sessionsAndMessages as any,
                );
            setErrors({ deleteChild, deleteParentAndChildren });
        }
    }, [email, parentSessions, sessionsAndMessages, sessionAndMessagesContext]);

    const breakIt = useCallback(async () => {
        await sessionAndMessagesContext?.store?.storeJustParent?.({ broken: true } as any, email);
    }, [sessionAndMessagesContext, email]);

    const nukeAllSessions = useCallback(async () => {
        async function deleteChildren() {
            try {
                const deleteParentAndChildren =
                    await sessionAndMessagesContext?.store?.deleteParentAndChildren?.(
                        sessionsAndMessages as any,
                    );
                return deleteParentAndChildren;
            } catch (e: any) {
                console.error(`Error in nukeAllSessions deleteChildren:`, e);
                return { errors: [`Error in nukeAllSessions deleteChildren: ${e?.message}`] };
            }
        }

        const deleteParentAndChildren = await deleteChildren();
        const reallyDelete = await sessionAndMessagesContext?.store?.deleteParent?.(email);
        setErrors({ deleteParentAndChildren, reallyDelete });
        isRecentChatIssue && window.location.reload();
    }, [email, isRecentChatIssue, sessionAndMessagesContext, sessionsAndMessages]);

    return useMemo(
        () => ({
            state: state as AnyObj | undefined,
            messages,
            sessionsAndMessages: sessionsAndMessages as AnyObj | undefined,
            parentSessions: parentSessions ?? [],
            errors,
            setErrors,
            ops: { deleteAllSessions, breakIt, nukeAllSessions },
        }),
        [
            state,
            messages,
            sessionsAndMessages,
            parentSessions,
            errors,
            deleteAllSessions,
            breakIt,
            nukeAllSessions,
        ],
    );
}

export function RecentChatIssueBanner() {
    const { ops } = useSessionsAndMessagesData(true);
    return <div style={{ padding: "1rem", borderRadius: "8px" }}>
        <div style={{ padding: "0.5rem 0", fontWeight: "bold" }}>
            Some error has occured in fetching recent chats
        </div>
        <EonUiButton
            fullWidthOnMobile
            text="Please Click here to retry"
            rank="secondary"
            size="small"
            icon="reload"
            onClick={ops.nukeAllSessions}
        />
    </div>;

}

function SessionsAndMessages() {
    const { state, messages, errors, ops } = useSessionsAndMessagesData(false);
    const { Json } = useAttributeValueComponents();
    const { ClipHeight, TwoColumnAndRest } = useCommonComponents();
    const email = useUserDataEmail();
    return <ClipHeight maxHeight="400px" scrollable>
        <button onClick={ops.deleteAllSessions}>Delete all sessions for {email}</button>
        <button onClick={ops.nukeAllSessions}>Nuke all sessions for {email}</button>
        <button onClick={ops.breakIt}>Break for {email}</button>
        <TwoColumnAndRest>
            <Json rootId="devmode.sessions" attribute="devmode.sessions.sessions" value={state} />
            <Json rootId="devmode.messages" attribute="devmode.sessions.messages" value={messages} />
            <Json rootId="devmode.errors" attribute="devmode.sessions.errors" value={errors} />
        </TwoColumnAndRest>
    </ClipHeight>;
}

export type CryptoProps = {
    id: string
}

export function Crypto({ id }: CryptoProps) {
    const store = useCrytoStore();
    const [crypto, setCrypto] = useState<any>({ loading: true });
    const [secret, setSecret] = useState<any>({ loading: true });
    const { ClipHeight, TwoColumnAndRest } = useCommonComponents();
    const { Json } = useAttributeValueComponents();
    const { H3 } = useRenderers();
    useEffect(() => {
        store.metaData(id).then(res => setCrypto(res));
        store.secret(id).then(res => setSecret(res));
    }, [id]);

    return <>
        <H3 rootId={"devmode.crypto"} attribute={"devmode.crypto.id"} value={`Crypto for ${id}`} />
        <TwoColumnAndRest rootId={"devmode.crypto"}>
            <Json rootId="devmode.crypto.meta" attribute="devmode.crypto.meta" value={crypto} />
            <Json rootId="devmode.crypto.secret" attribute="devmode.crypto.secret" value={secret} />
        </TwoColumnAndRest></>;
}

type TextAreaWithEnterProps = {
    value: string;
    onChange: (val: string) => void;
    onEnter?: () => void;
    placeholder?: string;
    rows?: number;
};

export function TextAreaWithEnter({
                                      value,
                                      onChange,
                                      onEnter,
                                      placeholder,
                                      rows = 4,
                                  }: TextAreaWithEnterProps) {
    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            onEnter?.();
        }
    };

    return (
        <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={rows}
            style={{ width: "100%", fontFamily: "monospace" }}
        />
    );
}

export function EncryptDecrypt({ id }: CryptoProps) {
    const store = useCrytoStore();
    const [crypto, setCrypto] = useState<any>({ loading: true });
    const [secret, setSecret] = useState<any>({ loading: true });
    const { TwoColumnAndRest } = useCommonComponents();
    const { Json } = useRenderers();
    useEffect(() => {
        store.metaData(id).then(res => setCrypto(res));
        store.secret(id).then(res => setSecret(res));
    }, [id]);

    const [plaintext, setPlaintext] = useState("");
    const [ciphertext, setCiphertext] = useState("");

    if (!isValue(crypto) || !isValue(secret)) {
        return (<Json rootId="devmode.crypto.loading" attribute="devmode.crypto.loading" value={crypto} />);
    }

    function encrypt() {
        const iv = crypto.value.iv;
        aesEncodeFn(secret.value, iv)(plaintext).then(setCiphertext);
    }

    function decrypt() {
        const iv = crypto.value.iv;
        aesDecodeFn(secret.value, iv)(ciphertext).then(setPlaintext);
    }

    return (
        <TwoColumnAndRest rootId="devmode.crypto">
            <TextAreaWithEnter
                value={plaintext}
                onChange={setPlaintext}
                onEnter={encrypt}
                placeholder="Enter plaintext, then press enter..."
            />
            <TextAreaWithEnter
                value={ciphertext}
                onChange={setCiphertext}
                onEnter={decrypt}
                placeholder="Enter ciphertext, then press enter..."
            />
        </TwoColumnAndRest>
    );
}

export function SessionsWithRecentChatIssue({ isRecentChatIssue }: { isRecentChatIssue?: boolean }) {
    return isRecentChatIssue ? <RecentChatIssueBanner /> : <SessionsAndMessages />;
}

export function SessionsDevMode() {
    const selectOps = useState(SessionsMode[0]);
    const selected = selectOps[0];
    const [sessionData] = useSessionAndMessages();
    const { parentId, childId } = sessionData || {};
    return <>
        <SimpleSelectableButtonBar options={SessionsMode} selectedOps={selectOps} rootId="sessions.mode" />
        {selected === "SessionsAndMessage" && <SessionsAndMessages />}
        {selected === "SessionCrypto" && <Crypto id={parentId} />}
        {selected === "MessagesCrypto" && <Crypto id={childId} />}
        {selected === "SessionEncryptDecrypt" && <EncryptDecrypt id={parentId} />}
        {selected === "MessagesEncryptDecrypt" && <EncryptDecrypt id={childId} />}
    </>;

}
